import Immutable from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import CancelHoldButton from 'app/components/shared/CancelHoldButton';
import { itemTransactionConstants, itemTransactionStatus } from '@bibliocommons/utils-item';
import { bibShape } from '@bibliocommons/bc-prop-types';
import { MATERIAL_TYPE_DIGITAL } from 'app/constants/BibConstants';
import * as CheckoutActions from 'app/actions/CheckoutActions';
import * as HoldActions from 'app/actions/HoldActions';
import ModalActions from 'app/actions/ModalActions';
import ShelfActions from 'app/actions/ShelfActions';
import { HOLD_STATUS_TYPES } from 'app/constants/HoldsConstants';
import { selectCurrentAccount } from 'app/selectors/AuthSelector';
import { selectActiveHoldsByMetadataId } from 'app/selectors/HoldsSelector';
import { selectCheckoutsByMetadataId } from 'app/selectors/CheckoutSelector';
import { selectBibEntities } from 'app/selectors/EntitiesSelector';
import RequestItemButton from '../RequestItemButton';
import { CheckedOutReadingLinks } from '../ItemTransactions';

export function ItemTransactionButtonContainer(props) {
  const {
    holdable,
    hold,
    isLoading,
    id,
    holdActions,
    checkoutActions,
    availableForCheckout,
    checkout,
    materialType,
    materialPolicy,
    accountId,
    format,
    title,
    authors,
    hasItemLevelHoldableItems,
    titleNotOwned,
    workflowId,
    messagingId,
    provider,
    bib,
    modalActions,
    shelfActions
  } = props;

  let element;
  const isDigitalHoldReady =
    hold && hold.get('status') === HOLD_STATUS_TYPES.READY_FOR_PICKUP && materialType === MATERIAL_TYPE_DIGITAL;

  if (!holdable) {
    return null;
  }

  const status = itemTransactionStatus(hold, materialType, hasItemLevelHoldableItems, checkout);

  switch (status) {
    case itemTransactionConstants.CANCEL_HOLD_BUTTON:
      element = (
        <CancelHoldButton
          metadataId={id}
          cancelHold={() => holdActions.startCancelHold(workflowId || id, id, messagingId)}
          isLoading={isLoading}
        />
      );
      break;
    case itemTransactionConstants.DIGITAL_READING_LINKS:
      element = (
        <CheckedOutReadingLinks
          bib={bib}
          checkInEnabled={checkout.get('checkInEnabled')}
          downloadable={checkout.get('downloadable')}
          format={checkout.get('format')}
          downloadUrl={checkout.get('downloadUrl')}
          thirdPartyReaderUrl={checkout.get('thirdPartyReaderUrl')}
          policy={materialPolicy}
          provider={provider}
          metadataId={id}
          messagingId={messagingId}
          checkoutId={checkout.get('checkoutId')}
          accountId={accountId}
          checkIn={checkoutActions.digitalCheckIn}
          startDigitalMissingFormatSelection={() =>
            checkoutActions.startDigitalMissingFormatSelection(workflowId || id, id, messagingId)}
          modalActions={modalActions}
          shelfActions={shelfActions}
        />
      );
      break;
    case itemTransactionConstants.REQUEST_ITEM_BUTTON:
    default:
      element = (
        <RequestItemButton
          metadataId={id}
          handleHoldClick={() =>
            holdActions.startPlaceHold({ workflowId: workflowId || id, metadataId: id, messagingId })}
          isLoading={isLoading}
          availableForCheckout={availableForCheckout || isDigitalHoldReady}
          materialType={materialType}
          handleCheckoutClick={() => checkoutActions.startDigitalCheckout(workflowId || id, id, messagingId)}
          format={format}
          title={title}
          treatAsItemLevelHold={hasItemLevelHoldableItems && materialPolicy.get('itemLevelHoldsPredetermined')}
          authors={authors}
          serviceAvailable={materialPolicy.get('healthy')}
          titleNotOwned={titleNotOwned}
        />
      );
  }

  return <div className="item-transaction-button-wrap">{element}</div>;
}

ItemTransactionButtonContainer.propTypes = {
  hold: ImmutablePropTypes.mapContains({
    id: PropTypes.string,
    status: PropTypes.string
  }),
  workflowId: PropTypes.string,
  messagingId: PropTypes.string,
  provider: PropTypes.string,
  holdable: PropTypes.bool,
  isLoading: PropTypes.bool,
  holdActions: PropTypes.objectOf(PropTypes.func).isRequired,
  checkoutActions: PropTypes.objectOf(PropTypes.func).isRequired,
  materialPolicy: ImmutablePropTypes.map,
  materialType: PropTypes.string,
  checkout: ImmutablePropTypes.mapContains({
    id: PropTypes.string
  }),
  accountId: PropTypes.number,
  availableForCheckout: PropTypes.bool,
  id: PropTypes.string.isRequired,
  format: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  authors: ImmutablePropTypes.listOf(PropTypes.string),
  hasItemLevelHoldableItems: PropTypes.bool,
  titleNotOwned: PropTypes.bool,
  bib: bibShape,
  modalActions: PropTypes.objectOf(PropTypes.func),
  shelfActions: PropTypes.objectOf(PropTypes.func)
};

ItemTransactionButtonContainer.displayName = 'ItemTransactionButtonContainer';

const mapStateToProps = (state, ownProps) => {
  const account = selectCurrentAccount(state, ownProps);
  const bib = selectBibEntities(state).get(ownProps.id);
  const policy = state.getIn(['entities', 'bibs', ownProps.id, 'policy'], Immutable.Map());
  const holds = selectActiveHoldsByMetadataId(state).get(ownProps.id, Immutable.List());
  const availabilityStatus = state.getIn(['entities', 'bibs', ownProps.id, 'availability', 'status']);
  const checkouts = selectCheckoutsByMetadataId(state).get(ownProps.id, Immutable.List());

  return {
    bib,
    accountId: account && account.get('id'),
    hold: holds.size === 1 ? holds.first() : null,
    holdable: state.getIn(['entities', 'bibs', ownProps.id, 'policy', 'holdable'], false),
    isLoading: state.getIn(['ui', 'itemTransactions', ownProps.id, 'isLoading'], false),
    checkout: checkouts.first(),
    availableForCheckout: availabilityStatus === 'AVAILABLE',
    materialType: state.getIn(['entities', 'bibs', ownProps.id, 'policy', 'materialType']),
    materialPolicy: policy.get('materialPolicy', Immutable.Map()),
    provider: policy.get('provider'),
    format: bib.getIn(['briefInfo', 'format']),
    title: bib.getIn(['briefInfo', 'title']),
    authors: bib.getIn(['briefInfo', 'authors']),
    hasItemLevelHoldableItems: policy.get('itemLevelHoldableItems'),
    titleNotOwned: availabilityStatus === 'RESOURCE_NOT_FOUND'
  };
};

const mapDispatchToProps = dispatch => ({
  holdActions: bindActionCreators(HoldActions, dispatch),
  checkoutActions: bindActionCreators(CheckoutActions, dispatch),
  modalActions: bindActionCreators(ModalActions, dispatch),
  shelfActions: bindActionCreators(ShelfActions, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(ItemTransactionButtonContainer);
